import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE, T } from '../constants';
import { authFetch } from '../utils/api';

const NY_TZ = 'America/New_York';

function _todayNY() {
  const d = new Date();
  const ny = new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
  return ny; // YYYY-MM-DD
}

const ACTION_STYLE = {
  TRADE:   { bg: '#dcfce7', text: '#15803d', label: 'TRADE'   },
  ENTRY:   { bg: '#dcfce7', text: '#15803d', label: 'ENTRY'   },
  GO:      { bg: '#dcfce7', text: '#15803d', label: 'GO'      },
  OBSERVE: { bg: '#dbeafe', text: '#1d4ed8', label: 'OBSERVE' },
  EXIT:    { bg: '#e5e7eb', text: '#374151', label: 'EXIT'    },
  BLOCK:   { bg: '#fee2e2', text: '#b91c1c', label: 'BLOCK'   },
  SKIP:    { bg: '#fef3c7', text: '#92400e', label: 'SKIP'    },
};

function _actionPill(action) {
  const s = ACTION_STYLE[action] || { bg: '#f1f5f9', text: '#475569', label: action || '—' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: 999,
      background: s.bg,
      color: s.text,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

export default function SignalAudit({ onClose }) {
  const [date, setDate]               = useState(_todayNY());
  const [blocksOnly, setBlocksOnly]   = useState(false);
  const [rows, setRows]               = useState([]);
  const [summary, setSummary]         = useState({ by_action: {}, by_strategy: {} });
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [expanded, setExpanded]       = useState({});  // strategy -> bool

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ date });
      if (blocksOnly) params.set('blocks_only', '1');
      const res = await authFetch(`/strategies/api/strategies/signal-audit/?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        setRows([]); setSummary({ by_action: {}, by_strategy: {} }); setTotal(0);
      } else {
        setRows(data.rows || []);
        setSummary(data.summary || { by_action: {}, by_strategy: {} });
        setTotal(data.total || 0);
        // Auto-expand all strategies that have rows
        const exp = {};
        (data.rows || []).forEach(r => { exp[r.strategy] = true; });
        setExpanded(exp);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [date, blocksOnly]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  // Group rows by strategy for the per-section tables
  const byStrategy = useMemo(() => {
    const out = {};
    for (const r of rows) {
      if (!out[r.strategy]) out[r.strategy] = [];
      out[r.strategy].push(r);
    }
    return out;
  }, [rows]);

  const strategies = useMemo(() => Object.keys(byStrategy).sort(), [byStrategy]);

  const toggleStrategy = (s) => setExpanded(e => ({ ...e, [s]: !e[s] }));

  const summaryCards = [
    { key: 'TRADE',   label: 'Trades',   color: '#16a34a' },
    { key: 'OBSERVE', label: 'Observe',  color: '#2563eb' },
    { key: 'BLOCK',   label: 'Blocks',   color: '#dc2626' },
    { key: 'SKIP',    label: 'Skips',    color: '#d97706' },
    { key: 'EXIT',    label: 'Exits',    color: '#6b7280' },
  ];

  return (
    <div style={{
      flex: 1,
      minHeight: 0,           // critical for flex-child overflow to actually work
      overflowY: 'auto',
      padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)',
      background: T.bg,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: T.textDim }}>
          Signal Audit
        </h1>
        <span style={{ fontSize: 12, color: T.textMuted }}>
          Every alert IMOLE received today + the filter that decided its fate
        </span>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              padding: '5px 12px',
              fontSize: 11,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              background: 'transparent',
              color: T.textDim,
              cursor: 'pointer',
            }}
          >← Back to Dashboard</button>
        )}
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '12px 16px', marginBottom: 14,
        background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 8,
      }}>
        <label style={{ fontSize: 12, color: T.textDim }}>
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              marginLeft: 8,
              padding: '4px 8px',
              fontSize: 12,
              border: `1px solid ${T.border}`,
              borderRadius: 4,
              background: T.bg,
              color: T.textDim,
            }}
          />
        </label>
        <label style={{ fontSize: 12, color: T.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={blocksOnly}
            onChange={(e) => setBlocksOnly(e.target.checked)}
          />
          Show blocks &amp; skips only
        </label>
        <button
          onClick={fetchAudit}
          style={{
            padding: '5px 12px',
            fontSize: 11,
            fontWeight: 600,
            border: 'none',
            borderRadius: 4,
            background: 'rgba(124,58,237,0.12)',
            color: '#7c3aed',
            cursor: 'pointer',
          }}
        >{loading ? 'Loading…' : 'Refresh'}</button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textMuted }}>
          {total} {total === 1 ? 'signal' : 'signals'} on {date}
        </span>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {summaryCards.map(({ key, label, color }) => {
          const count = summary.by_action?.[key] || 0;
          return (
            <div key={key} style={{
              flex: '1 1 110px',
              minWidth: 100,
              padding: '12px 14px',
              background: T.cardBg,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.1 }}>
                {count}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted, marginTop: 4 }}>
                {label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 14px',
          marginBottom: 12,
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          color: '#b91c1c',
          fontSize: 12,
        }}>
          Error: {error}
        </div>
      )}

      {/* Per-strategy tables */}
      {strategies.length === 0 && !loading && !error && (
        <div style={{
          padding: '24px 14px',
          background: T.cardBg,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          color: T.textMuted,
          fontSize: 13,
          fontStyle: 'italic',
          textAlign: 'center',
        }}>
          No signals on {date}.
        </div>
      )}

      {strategies.map((strategy) => {
        const sRows = byStrategy[strategy];
        const counts = sRows.reduce((acc, r) => {
          acc[r.action] = (acc[r.action] || 0) + 1;
          return acc;
        }, {});
        const isOpen = expanded[strategy] !== false;
        return (
          <div key={strategy} style={{
            marginBottom: 14,
            background: T.cardBg,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}>
            {/* Strategy header (collapsible) */}
            <div
              onClick={() => toggleStrategy(strategy)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px',
                background: T.bg,
                borderBottom: isOpen ? `1px solid ${T.border}` : 'none',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{
                display: 'inline-block',
                width: 14,
                fontSize: 10,
                color: T.textMuted,
                transform: isOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.15s',
              }}>▶</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.textDim }}>
                {strategy}
              </span>
              <span style={{ fontSize: 11, color: T.textMuted }}>
                {sRows.length} {sRows.length === 1 ? 'signal' : 'signals'}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {Object.entries(counts).map(([action, n]) => (
                  <span key={action} style={{ fontSize: 10 }}>
                    {_actionPill(action)}
                    <span style={{ marginLeft: 4, color: T.textMuted, fontWeight: 700 }}>{n}</span>
                  </span>
                ))}
              </div>
            </div>

            {isOpen && (
              <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.bg }}>
                    <th style={_thStyle}>ET</th>
                    <th style={_thStyle}>Sub-system</th>
                    <th style={_thStyle}>Action</th>
                    <th style={_thStyle}>Grade</th>
                    <th style={_thStyle}>Ct</th>
                    <th style={_thStyle}>Raw reason</th>
                    <th style={_thStyle}>Plain English</th>
                  </tr>
                </thead>
                <tbody>
                  {sRows.map((r, i) => (
                    <tr key={i} style={{
                      background: i % 2 === 0 ? T.cardBg : T.bg,
                    }}>
                      <td style={{ ..._tdStyle, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {r.et_time || '—'}
                      </td>
                      <td style={{ ..._tdStyle, fontFamily: 'monospace', color: T.textMuted }}>
                        {r.subsystem || '—'}
                      </td>
                      <td style={_tdStyle}>{_actionPill(r.action)}</td>
                      <td style={{ ..._tdStyle, fontFamily: 'monospace', color: T.textMuted }}>
                        {r.grade || '—'}
                      </td>
                      <td style={{ ..._tdStyle, fontFamily: 'monospace', textAlign: 'center' }}>
                        {r.contracts || 0}
                      </td>
                      <td style={{ ..._tdStyle, fontFamily: 'monospace', fontSize: 11, color: T.textMuted, maxWidth: 240, wordBreak: 'break-word' }}>
                        {r.raw_reason || '—'}
                      </td>
                      <td style={{ ..._tdStyle, fontSize: 12, color: T.textDim, lineHeight: 1.45 }}>
                        {r.human_reason || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

const _thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#6b7280',
  borderBottom: `1px solid ${T.border}`,
};
const _tdStyle = {
  padding: '9px 12px',
  fontSize: 12,
  borderBottom: `1px solid ${T.border}`,
  verticalAlign: 'top',
};
